import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import map from "lodash/map";
import NameCase from "namecase";

import { executeGet } from "../../Api";
import { updateUserData, getAdminUserData, updateAdminData } from "../../Actions/authActions";
import { subDomain } from "../../utils";
import { CreditCardIcon } from "../../Components/Icons";
import CustomCheckbox from "../../Components/CustomCheckBox";
import ReactTableServerSide from "./ReactTable/ReactTableServerSide";
import "./payersTable.css";

function formatDate(val) {
    if (!val) return "—";
    const d = new Date(val);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLastSuccessMethod(methods) {
    if (!methods || !methods.length) return null;
    const withSuccess = methods.filter(pm => pm.lastSuccessAt);
    if (!withSuccess.length) return null;
    return withSuccess.reduce((best, pm) =>
        new Date(pm.lastSuccessAt) > new Date(best.lastSuccessAt) ? pm : best
    );
}

const PaymentMethodCard = ({ pm, paymentGatewayType, onSetDefault, isLastSuccess, onMakeDefault }) => {
    let displayLabel = "";
    if (paymentGatewayType === "NMI") {
        if (pm.cardType && pm.cardNumber) {
            let cardTypeDisplay = pm.cardType.toUpperCase();
            if (cardTypeDisplay === "MASTERCARD") cardTypeDisplay = "MC";
            displayLabel = `${cardTypeDisplay} ${pm.cardNumber}`;
        } else if (pm.paymentType === "eCheck" && pm.bankAccountNumber) {
            displayLabel = `eCheck ${pm.bankAccountNumber.slice(-4)}`;
        }
    } else if (paymentGatewayType === "converge") {
        if (pm.ssl_card_short_description) {
            displayLabel = `${pm.ssl_card_short_description} ${(pm.ssl_card_number || "").slice(-4)}`;
        } else if (pm.ssl_bank_account_number) {
            displayLabel = `eCheck ${pm.ssl_bank_account_number.slice(-4)}`;
        }
    }

    return (
        <div className="nav-item custom-tab border-1 mb-2" style={{ display: "inline-block", minWidth: 180, marginRight: 8 }}>
            <div className="nav-link text-center" style={{ padding: "8px 12px" }}>
                <div className="check-btn d-flex mb-1 align-items-center justify-content-center">
                    <CreditCardIcon />
                    <span className="text-dark payment-method-text ms-1" style={{ fontWeight: 500 }}>
                        {displayLabel || "Card"}
                    </span>
                </div>
                {isLastSuccess && (
                    <div className="mb-1" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                        <span style={{
                            fontSize: "0.68rem",
                            background: "#d4edda",
                            color: "#155724",
                            borderRadius: 4,
                            padding: "1px 6px",
                            fontWeight: 600,
                            letterSpacing: "0.01em",
                        }}>
                            Last successful charge
                        </span>
                        {!pm.isDefault && (
                            <button
                                onClick={onMakeDefault}
                                style={{
                                    fontSize: "0.65rem",
                                    padding: "1px 7px",
                                    background: "#155724",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    lineHeight: 1.6,
                                }}
                            >
                                Make default
                            </button>
                        )}
                    </div>
                )}
                <div className="mb-1 text-start text-dark" style={{ fontSize: "0.78rem" }}>
                    <label className="font-weight-light label-check sos-checkbox d-flex align-items-center" style={{ cursor: "pointer" }}>
                        <CustomCheckbox
                            checked={pm.isDefault || false}
                            onChange={(e) => onSetDefault(e, pm)}
                        />
                        <span className="payment-style payment-method-text ms-1">Default</span>
                    </label>
                </div>
                <div className="divider mb-1" />
                <div style={{ fontSize: "0.72rem", color: "#555", textAlign: "left", lineHeight: 1.6 }}>
                    <div><span style={{ fontWeight: 500 }}>Last attempted:</span> {formatDate(pm.lastAttemptedAt)}</div>
                    <div><span style={{ fontWeight: 500 }}>Last success:</span> {formatDate(pm.lastSuccessAt)}</div>
                    <div><span style={{ fontWeight: 500 }}>Last failed:</span> {formatDate(pm.lastFailedAt)}</div>
                </div>
            </div>
        </div>
    );
};

const SEARCH_OPTIONS = [
    { value: "payerName", label: "Name" },
    { value: "payerEmail", label: "Email" },
    { value: "payerCell", label: "Phone" },
];

const PaymentMethodsTable = ({ themeColor1, userAdmin }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const subdomain = params.subdomain || subDomain();
    const isMountedRef = useRef(true);

    const adminUser = useSelector((state) => state.auth.adminUser);
    const paymentGatewayType = adminUser?.paymentGatewayType || "NMI";

    const [localMethods, setLocalMethods] = useState({});
    const [isSavingToggle, setIsSavingToggle] = useState(false);
    const [showFailedDefault, setShowFailedDefault] = useState(false);
    const [searchField, setSearchField] = useState("payerName");
    const [searchInput, setSearchInput] = useState("");
    const [activeSearch, setActiveSearch] = useState({ by: null, term: null });

    const chargeNowFallback = adminUser?.chargeNowFallbackToLastSuccess === true;

    // Derive external search/filter state to pass to the table
    // Failed-default filter takes precedence; otherwise use the search input
    let externalSearchBy = null;
    let externalSearchTerm = null;
    if (showFailedDefault) {
        externalSearchBy = "defaultFailedAt";
        externalSearchTerm = "true";
    } else if (activeSearch.by && activeSearch.term) {
        externalSearchBy = activeSearch.by;
        externalSearchTerm = activeSearch.term;
    }

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (adminUser && !Object.keys(adminUser).length) {
            dispatch(getAdminUserData(subdomain));
        }
    }, [!adminUser]);

    const handleChargeNowFallbackToggle = async (e) => {
        const checked = e.target.checked;
        setIsSavingToggle(true);
        try {
            await dispatch(updateAdminData({ chargeNowFallbackToLastSuccess: checked }));
        } finally {
            if (isMountedRef.current) setIsSavingToggle(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setActiveSearch({ by: searchInput.trim() ? searchField : null, term: searchInput.trim() || null });
    };

    const handleSearchClear = () => {
        setSearchInput("");
        setActiveSearch({ by: null, term: null });
    };

    const fetchUsers = async ({ currentPage, limit, sortBy, sortDirection, searchTerm, searchBy }) => {
        try {
            const buildQuery = { subdomain, page: currentPage, limit, sortBy, sortDirection, searchTerm, searchBy };
            const url = `users/allUsers?${new URLSearchParams(buildQuery).toString()}`;
            const result = await executeGet(url, {}).then((res) => res?.data);
            return {
                data: result.users,
                total: result.totalCount,
                totalPages: result.totalPages,
                currentPage: result.page,
                pageSize: result.pageSize,
            };
        } catch (error) {
            console.error("Error fetching payment methods data:", error);
            return { data: [], total: 0 };
        }
    };

    const handleSetDefault = (e, payer, pm) => {
        const checked = e.target.checked;
        const currentMethods = localMethods[payer._id] || payer.paymentMethods || [];
        const updated = currentMethods.map((p) => ({
            ...p,
            isDefault: p._id === pm._id ? checked : false,
        }));
        setLocalMethods((prev) => ({ ...prev, [payer._id]: updated }));

        const updatedPayer = { ...payer, paymentMethods: updated };
        dispatch(updateUserData(updatedPayer));
        localStorage.setItem("payerUser", JSON.stringify(updatedPayer));
    };

    const columns = React.useMemo(() => [
        {
            id: "payerName",
            header: "Payer",
            enableSorting: true,
            accessorKey: "payerLast",
            size: 140,
            minSize: 140,
            maxSize: 160,
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{NameCase(`${row.payerFirst || ""} ${row.payerMiddle || ""}`.trim())} {NameCase(row.payerLast || "")}</div>
                        <div style={{ fontSize: "0.78rem", color: "#777" }}>{row.payerEmail && !row.payerEmail.startsWith("*fake*") ? row.payerEmail : ""}</div>
                    </div>
                );
            },
        },
        {
            id: "lastAttemptedAt",
            header: "Last Attempted",
            enableSorting: true,
            enableColumnFilter: false,
            accessorFn: (row) => {
                const def = (row.paymentMethods || []).find(pm => pm.isDefault);
                return def?.lastAttemptedAt || null;
            },
            size: 130,
            minSize: 110,
            cell: (props) => {
                const def = (props.row.original.paymentMethods || []).find(pm => pm.isDefault);
                return <span style={{ fontSize: "0.8rem", color: "#555" }}>{formatDate(def?.lastAttemptedAt)}</span>;
            },
        },
        {
            id: "lastFailedAt",
            header: "Last Failed",
            enableSorting: true,
            enableColumnFilter: false,
            accessorFn: (row) => {
                const def = (row.paymentMethods || []).find(pm => pm.isDefault);
                return def?.lastFailedAt || null;
            },
            size: 120,
            minSize: 100,
            cell: (props) => {
                const def = (props.row.original.paymentMethods || []).find(pm => pm.isDefault);
                return <span style={{ fontSize: "0.8rem", color: def?.lastFailedAt ? "#c0392b" : "#555" }}>{formatDate(def?.lastFailedAt)}</span>;
            },
        },
        {
            id: "paymentMethods",
            header: "Payment Methods",
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (row) => row._id,
            size: 600,
            minSize: 400,
            cell: (props) => {
                const payer = props.row.original;
                const methods = localMethods[payer._id] !== undefined
                    ? localMethods[payer._id]
                    : (payer.paymentMethods || []);

                if (!methods.length) {
                    return <span style={{ color: "#aaa", fontSize: "0.82rem" }}>No payment methods on file</span>;
                }

                const lastSuccessMethod = getLastSuccessMethod(methods);

                const sorted = lastSuccessMethod
                    ? [lastSuccessMethod, ...methods.filter(pm => pm._id !== lastSuccessMethod._id)]
                    : methods;

                return (
                    <div className="d-flex flex-wrap">
                        {map(sorted, (pm, i) => (
                            <PaymentMethodCard
                                key={pm._id || i}
                                pm={pm}
                                paymentGatewayType={paymentGatewayType}
                                onSetDefault={(e, pm) => handleSetDefault(e, payer, pm)}
                                isLastSuccess={lastSuccessMethod && pm._id === lastSuccessMethod._id}
                                onMakeDefault={() => handleSetDefault({ target: { checked: true } }, payer, pm)}
                            />
                        ))}
                    </div>
                );
            },
        },
    ], [localMethods, paymentGatewayType]);

    return (
        <div className="container-fluid">
            {/* ChargeNow fallback toggle */}
            <div style={{
                background: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                padding: "14px 20px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
            }}>
                <CustomCheckbox
                    checked={chargeNowFallback}
                    onChange={handleChargeNowFallbackToggle}
                    disabled={isSavingToggle}
                />
                <span style={{ fontSize: "0.9rem", color: "#333" }}>
                    <strong>ChargeNow:</strong> if default payment method fails, charge last successful payment method
                </span>
                {isSavingToggle && <span style={{ fontSize: "0.8rem", color: "#888", marginLeft: 6 }}>Saving…</span>}
            </div>

            {/* Filter + search toolbar */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 14,
                flexWrap: "wrap",
            }}>
                {/* Failed-default filter button */}
                <button
                    onClick={() => setShowFailedDefault(v => !v)}
                    style={{
                        fontSize: "0.82rem",
                        padding: "5px 14px",
                        borderRadius: 5,
                        border: showFailedDefault ? "1.5px solid #c0392b" : "1.5px solid #ccc",
                        background: showFailedDefault ? "#fdecea" : "#fff",
                        color: showFailedDefault ? "#c0392b" : "#555",
                        fontWeight: showFailedDefault ? 700 : 400,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {showFailedDefault ? "✕ " : ""}Default method last failed
                </button>

                {/* Search field selector + input */}
                <form
                    onSubmit={handleSearchSubmit}
                    style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                >
                    <select
                        value={searchField}
                        onChange={e => { setSearchField(e.target.value); setSearchInput(""); setActiveSearch({ by: null, term: null }); }}
                        style={{
                            fontSize: "0.82rem",
                            padding: "5px 8px",
                            borderRadius: 5,
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        {SEARCH_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            placeholder={`Search by ${SEARCH_OPTIONS.find(o => o.value === searchField)?.label.toLowerCase()}…`}
                            style={{
                                fontSize: "0.82rem",
                                padding: "5px 28px 5px 10px",
                                borderRadius: 5,
                                border: activeSearch.term ? "1.5px solid #3a5fcb" : "1px solid #ccc",
                                width: 200,
                            }}
                        />
                        {searchInput && (
                            <button
                                type="button"
                                onClick={handleSearchClear}
                                style={{
                                    position: "absolute",
                                    right: 6,
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    color: "#888",
                                    lineHeight: 1,
                                    padding: 0,
                                }}
                            >×</button>
                        )}
                    </div>
                    <button
                        type="submit"
                        style={{
                            fontSize: "0.82rem",
                            padding: "5px 14px",
                            borderRadius: 5,
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Search
                    </button>
                </form>
            </div>

            <ReactTableServerSide
                fetchData={fetchUsers}
                columns={columns}
                initialSortState={{ sortBy: "payerLast", desc: false }}
                externalSearchBy={externalSearchBy}
                externalSearchTerm={externalSearchTerm}
            />
        </div>
    );
};

export default PaymentMethodsTable;
